import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {
    Mail,
    Lock,
    Eye,
    EyeOff,
    CheckCircle,
    AlertCircle,
    ArrowRight,
    Home,
    User,
    Building,
    Shield,
    Loader,
    Phone,
    MapPin,
    Check,
    X
} from 'lucide-react';
import {API_CONSTANTS} from "../../Constants";
import {useAuth} from "../../App";
import SEO from "../../Components/Common/SEO";

const Register = () => {
    const navigate = useNavigate();
    const {register} = useAuth();

    const handleNavigate = (path) => {
        console.log('Navigate to:', path);
        navigate(path);
    };

    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
        // Step 1: Account Type & Basic Info
        userType: 'tenant',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',

        // Step 2: Password & Security
        password: '',
        confirmPassword: '',

        // Step 3: Additional Info
        location: '',
        company: '', // For managers
        businessLicense: '', // For managers

        // Terms & Conditions
        agreeToTerms: false,
        agreeToPrivacy: false,
        subscribeNewsletter: true
    });

    const [userTypes, setUserTypes] = useState([]);
    const [isLoadingUserTypes, setIsLoadingUserTypes] = useState(true);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [registerStatus, setRegisterStatus] = useState(null);
    const [errors, setErrors] = useState({general: "All fields are required"});
    const [passwordStrength, setPasswordStrength] = useState({score: 0, feedback: []});

    // Fetch user types from API
    useEffect(() => {
        const fetchUserTypes = async () => {
            try {
                setIsLoadingUserTypes(true);
                const response = await fetch(API_CONSTANTS.BASE_URL + '/user-types/');

                if (!response.ok) {
                    throw new Error('Failed to fetch user types');
                }

                const data = await response.json();

                if (data.success) {
                    console.log("data.data");
                    console.log(data.data.userTypes.length);
                    setUserTypes(data.data.userTypes);
                    // Set default user type to the first one if available
                    if (data.data.userTypes.length > 0) {
                        setFormData(prev => ({
                            ...prev,
                            userType: data.data.userTypes[0].id
                        }));
                    }
                } else {
                    throw new Error(data.message || 'Failed to fetch user types');
                }
            } catch (error) {
                console.error('Error fetching user types:', error);
                // Fallback to default user types if API fails
                const defaultUserTypes = [
                    {
                        id: 'tenant',
                        label: 'Tenant',
                        description: 'Looking for a property to rent',
                        icon: 'User',
                        color: 'blue'
                    },
                    {
                        id: 'manager',
                        label: 'Property Manager',
                        description: 'Managing rental properties',
                        icon: 'Building',
                        color: 'green'
                    },
                    {
                        id: 'admin',
                        label: 'Administrator',
                        description: 'Platform administration',
                        icon: 'Shield',
                        color: 'purple'
                    }
                ];
                setUserTypes(defaultUserTypes);
                setErrors(prev => ({
                    ...prev,
                    userTypes: 'Failed to load user types. Using defaults.'
                }));
            } finally {
                setIsLoadingUserTypes(false);
            }
        };

        fetchUserTypes();
    }, []);

    // User type ids come from the backend (e.g. "4", "5") and aren't stable
    // across environments, so match on the label instead of the raw id.
    const isManagerUserType = () => {
        const selected = userTypes.find(t => t.id === formData.userType);
        return !!selected?.label?.toLowerCase().includes('manager');
    };

    const handleInputChange = (e) => {
        const {name, value, type, checked} = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }

        // Check password strength
        if (name === 'password') {
            checkPasswordStrength(value);
        }
    };

    const checkPasswordStrength = (password) => {
        const checks = [
            {test: password.length >= 8, message: 'At least 8 characters'},
            {test: /[A-Z]/.test(password), message: 'One uppercase letter'},
            {test: /[a-z]/.test(password), message: 'One lowercase letter'},
            {test: /\d/.test(password), message: 'One number'},
            {test: /[!@#$%^&*]/.test(password), message: 'One special character'}
        ];

        const score = checks.filter(check => check.test).length;
        const feedback = checks.map(check => ({
            message: check.message,
            valid: check.test
        }));

        setPasswordStrength({score, feedback});
    };

    const validateStep = (step) => {
        const newErrors = {};

        if (step === 1) {
            if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
            if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
            if (!formData.email) {
                newErrors.email = 'Email is required';
            } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
                newErrors.email = 'Please enter a valid email';
            }
            if (!formData.phone) {
                newErrors.phone = 'Phone number is required';
            } else if (!/^(07\d{8}|01\d{8}|2547\d{8}|2541\d{8})$/.test(formData.phone.replace(/\s/g, ''))) {
                newErrors.phone = 'Please enter a valid Kenyan phone number';
            }
        }

        if (step === 2) {
            if (!formData.password) {
                newErrors.password = 'Password is required';
            } else if (passwordStrength.score < 3) {
                newErrors.password = 'Password is too weak';
            }

            if (!formData.confirmPassword) {
                newErrors.confirmPassword = 'Please confirm your password';
            } else if (formData.password !== formData.confirmPassword) {
                newErrors.confirmPassword = 'Passwords do not match';
            }
        }

        if (step === 3) {
            if (!formData.location) newErrors.location = 'Location is required';
            if (!formData.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms';
            if (!formData.agreeToPrivacy) newErrors.agreeToPrivacy = 'You must agree to the privacy policy';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        if (validateStep(currentStep)) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handlePrevious = () => {
        setCurrentStep(currentStep - 1);
        setErrors({});
    };

    const handleSubmit = async () => {
        if (!validateStep(3)) return;
        setIsLoading(true);
        setRegisterStatus(null);
        setErrors({});

        try {
            const payload = {
                userType: formData.userType,
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                phone: formData.phone,
                password: formData.password,
                location: formData.location,
                ...(isManagerUserType() && {
                    company: formData.company,
                    businessLicense: formData.businessLicense
                })
            };

            await register(payload);

            setRegisterStatus('success');
            setTimeout(() => handleNavigate('/verify-email'), 2000);
        } catch (error) {
            setRegisterStatus('error');
            if (error.field) {
                setErrors({[error.field]: error.fieldMessage || error.message});
            } else {
                setErrors({general: error.message || 'Registration failed. Please try again.'});
            }
        } finally {
            setIsLoading(false);
        }
    };
    const kenyanCities = [
        'Nairobi', 'Mombasa', 'Kisumu', 'Nakuru', 'Eldoret', 'Thika', 'Malindi',
        'Kitale', 'Garissa', 'Kakamega', 'Machakos', 'Meru', 'Nyeri', 'Kericho'
    ];

    const getPasswordStrengthColor = () => {
        if (passwordStrength.score <= 2) return 'bg-murram-500';
        if (passwordStrength.score <= 3) return 'bg-dawn-500';
        return 'bg-highland-500';
    };

    const getPasswordStrengthText = () => {
        if (passwordStrength.score <= 2) return 'Weak';
        if (passwordStrength.score <= 3) return 'Medium';
        return 'Strong';
    };

    const getStepTitle = () => {
        switch (currentStep) {
            case 1:
                return 'Account Type & Basic Information';
            case 2:
                return 'Password & Security';
            case 3:
                return 'Complete Your Profile';
            default:
                return 'Create Account';
        }
    };

    // Helper function to get the appropriate icon component
    const getIconComponent = (iconName) => {
        const icons = {
            User,
            Building,
            Shield
        };
        return icons[iconName] || User;
    };

    return (
        <div className="h-screen overflow-hidden bg-karibu-100 flex items-center justify-center p-3">
            <SEO title="Create Account" canonicalPath="/register" noindex />
            {/* Background Pattern - Fixed to cover full page */}
            <div className="fixed inset-0 z-0">
                <div className="absolute inset-0 bg-gradient-to-br from-highland-50 to-karibu-200"/>
                <div className="absolute inset-0 opacity-10" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                }}/>
            </div>

            <div className="relative z-10 w-full max-w-6xl max-h-full flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-center mb-3">
                    <div
                        className="h-9 w-9 bg-highland-500 rounded-xl flex items-center justify-center mr-3">
                        <Home className="h-5 w-5 text-white"/>
                    </div>
                    <div className="text-left">
                        <h1 className="font-display text-lg font-bold text-soil-900 leading-tight">Join Rent Utopia</h1>
                        <p className="text-xs text-soil-600">
                            Create your account to get started
                        </p>
                    </div>
                </div>

                {/* Main Content */}
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                    <div className="lg:flex">
                        {/* Left Side - User Type Selection & Progress */}
                        <div
                            className="lg:w-1/3 bg-highland-700 p-4 flex flex-col justify-center">

                            {/* Progress Indicator */}
                            <div className="mb-4">
                                <div className="flex items-center justify-between mb-3">
                                    {[1, 2, 3].map((step, index) => (
                                        <React.Fragment key={step}>
                                            <div
                                                className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                                                    currentStep >= step
                                                        ? 'bg-white border-white text-highland-500'
                                                        : 'bg-white bg-opacity-20 border-white border-opacity-50 text-white'
                                                }`}>
                                                {currentStep > step ? (
                                                    <Check className="w-4 h-4"/>
                                                ) : (
                                                    <span className="text-sm font-medium">{step}</span>
                                                )}
                                            </div>
                                            {index < 2 && (
                                                <div className={`flex-1 h-1 mx-2 ${
                                                    currentStep > step ? 'bg-white' : 'bg-white bg-opacity-30'
                                                }`}/>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </div>
                                <div className="flex justify-between text-xs text-karibu-200/80">
                                    <span>Account</span>
                                    <span>Security</span>
                                    <span>Complete</span>
                                </div>
                            </div>

                            <div className="text-center lg:text-left mb-3">
                                <h2 className="font-display text-base font-bold text-white mb-1">Choose Your Role</h2>
                                <p className="text-xs text-karibu-200/80">Select how you'll be using Rent Utopia</p>
                            </div>

                            <div className="space-y-2">
                                {isLoadingUserTypes ? (
                                    <div className="flex items-center justify-center py-4">
                                        <Loader className="w-6 h-6 text-white animate-spin"/>
                                        <span className="ml-2 text-sm text-white">Loading user types...</span>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {userTypes.map((type) => {
                                            const IconComponent = getIconComponent(type.icon);
                                            return (
                                                <label
                                                    key={type.id}
                                                    className={`relative flex items-center p-2.5 rounded-xl cursor-pointer transition-all duration-200 ${
                                                        formData.userType === type.id
                                                            ? 'bg-white bg-opacity-20 border-2 border-white'
                                                            : 'bg-white bg-opacity-10 border-2 border-transparent hover:bg-opacity-15'
                                                    }`}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="userType"
                                                        value={type.id}
                                                        checked={formData.userType === type.id}
                                                        onChange={handleInputChange}
                                                        className="sr-only"
                                                    />
                                                    <div
                                                        className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
                                                            formData.userType === type.id
                                                                ? 'bg-white'
                                                                : 'bg-white bg-opacity-20'
                                                        }`}>
                                                        <IconComponent className={`w-4 h-4 ${
                                                            formData.userType === type.id
                                                                ? 'text-highland-500'
                                                                : 'text-white'
                                                        }`}/>
                                                    </div>
                                                    <div className="ml-3 flex-1">
                                                        <div className="text-sm font-semibold text-white">{type.label}</div>
                                                        <div className="text-xs text-karibu-200/80">{type.description}</div>
                                                    </div>
                                                </label>
                                            );
                                        })}
                                    </div>
                                )}

                                {errors.userTypes && (
                                    <div
                                        className="mt-3 p-2 bg-murram-500/20 border border-murram-300/50 rounded-lg">
                                        <p className="text-karibu-100 text-xs">{errors.userTypes}</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Right Side - Registration Form */}
                        <div className="lg:w-2/3 p-4 lg:p-6 flex flex-col justify-center">
                            {/* Status Messages */}
                            {registerStatus === 'success' && (
                                <div
                                    className="mb-3 p-2.5 bg-highland-50 border border-highland-100 rounded-lg flex items-center space-x-2">
                                    <CheckCircle className="w-4 h-4 text-highland-500 flex-shrink-0"/>
                                    <p className="text-sm text-highland-600">Registration successful! Check your email to verify
                                        your account.</p>
                                </div>
                            )}

                            {registerStatus === 'error' && (
                                <div
                                    className="mb-3 p-2.5 bg-murram-100/40 border border-murram-100 rounded-lg flex items-center space-x-2">
                                    <AlertCircle className="w-4 h-4 text-murram-500 flex-shrink-0"/>
                                    <p className="text-sm text-murram-600">{errors.general}</p>
                                </div>
                            )}

                            <div className="max-w-md mx-auto lg:mx-0 w-full">
                                <h3 className="font-display text-lg font-bold text-soil-900 mb-3 text-center lg:text-left">
                                    {getStepTitle()}
                                </h3>

                                <div className="space-y-3">
                                    {/* Step 1: Basic Information */}
                                    {currentStep === 1 && (
                                        <>
                                            {/* Name Fields */}
                                            <div className="grid grid-cols-2 gap-3">
                                                <div>
                                                    <label className="block text-xs font-medium text-soil-600 mb-1">
                                                        First Name
                                                    </label>
                                                    <input
                                                        name="firstName"
                                                        type="text"
                                                        value={formData.firstName}
                                                        onChange={handleInputChange}
                                                        className={`block w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent transition-colors ${
                                                            errors.firstName ? 'border-murram-500' : 'border-soil-100'
                                                        }`}
                                                        placeholder="John"
                                                    />
                                                    {errors.firstName && (
                                                        <p className="mt-1 text-xs text-murram-600">{errors.firstName}</p>
                                                    )}
                                                </div>

                                                <div>
                                                    <label className="block text-xs font-medium text-soil-600 mb-1">
                                                        Last Name
                                                    </label>
                                                    <input
                                                        name="lastName"
                                                        type="text"
                                                        value={formData.lastName}
                                                        onChange={handleInputChange}
                                                        className={`block w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent transition-colors ${
                                                            errors.lastName ? 'border-murram-500' : 'border-soil-100'
                                                        }`}
                                                        placeholder="Doe"
                                                    />
                                                    {errors.lastName && (
                                                        <p className="mt-1 text-xs text-murram-600">{errors.lastName}</p>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Email */}
                                            <div>
                                                <label className="block text-xs font-medium text-soil-600 mb-1">
                                                    Email Address
                                                </label>
                                                <div className="relative">
                                                    <div
                                                        className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <Mail className="h-4 w-4 text-soil-400"/>
                                                    </div>
                                                    <input
                                                        name="email"
                                                        type="email"
                                                        autoComplete="email"
                                                        value={formData.email}
                                                        onChange={handleInputChange}
                                                        className={`block w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent transition-colors ${
                                                            errors.email ? 'border-murram-500' : 'border-soil-100'
                                                        }`}
                                                        placeholder="john@example.com"
                                                    />
                                                </div>
                                                {errors.email && (
                                                    <p className="mt-1 text-xs text-murram-600">{errors.email}</p>
                                                )}
                                            </div>

                                            {/* Phone */}
                                            <div>
                                                <label className="block text-xs font-medium text-soil-600 mb-1">
                                                    Phone Number
                                                </label>
                                                <div className="relative">
                                                    <div
                                                        className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <Phone className="h-4 w-4 text-soil-400"/>
                                                    </div>
                                                    <input
                                                        name="phone"
                                                        type="tel"
                                                        value={formData.phone}
                                                        onChange={handleInputChange}
                                                        className={`block w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent transition-colors ${
                                                            errors.phone ? 'border-murram-500' : 'border-soil-100'
                                                        }`}
                                                        placeholder="+254 700 123 456"
                                                    />
                                                </div>
                                                {errors.phone && (
                                                    <p className="mt-1 text-xs text-murram-600">{errors.phone}</p>
                                                )}
                                            </div>
                                        </>
                                    )}

                                    {/* Step 2: Password & Security */}
                                    {currentStep === 2 && (
                                        <>
                                            {/* Password */}
                                            <div>
                                                <label className="block text-xs font-medium text-soil-600 mb-1">
                                                    Password
                                                </label>
                                                <div className="relative">
                                                    <div
                                                        className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <Lock className="h-4 w-4 text-soil-400"/>
                                                    </div>
                                                    <input
                                                        name="password"
                                                        type={showPassword ? 'text' : 'password'}
                                                        autoComplete="new-password"
                                                        value={formData.password}
                                                        onChange={handleInputChange}
                                                        className={`block w-full pl-10 pr-10 py-2 border rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent transition-colors ${
                                                            errors.password ? 'border-murram-500' : 'border-soil-100'
                                                        }`}
                                                        placeholder="Create a strong password"
                                                    />
                                                    <button
                                                        type="button"
                                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                                        onClick={() => setShowPassword(!showPassword)}
                                                    >
                                                        {showPassword ? (
                                                            <EyeOff className="h-4 w-4 text-soil-400"/>
                                                        ) : (
                                                            <Eye className="h-4 w-4 text-soil-400"/>
                                                        )}
                                                    </button>
                                                </div>
                                                {errors.password && (
                                                    <p className="mt-1 text-xs text-murram-600">{errors.password}</p>
                                                )}

                                                {/* Password Strength Indicator */}
                                                {formData.password && (
                                                    <div className="mt-1.5">
                                                        <div className="flex items-center justify-between mb-1">
                                                            <span
                                                                className="text-xs text-soil-600">Password strength:</span>
                                                            <span className={`text-xs font-medium ${
                                                                passwordStrength.score <= 2 ? 'text-murram-600' :
                                                                    passwordStrength.score <= 3 ? 'text-dawn-600' : 'text-highland-600'
                                                            }`}>
                                                                {getPasswordStrengthText()}
                                                            </span>
                                                        </div>
                                                        <div className="w-full bg-soil-100 rounded-full h-1.5">
                                                            <div
                                                                className={`h-1.5 rounded-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                                                                style={{width: `${(passwordStrength.score / 5) * 100}%`}}
                                                            />
                                                        </div>
                                                        <div className="mt-1.5 space-y-0.5">
                                                            {passwordStrength.feedback.map((item, index) => (
                                                                <div key={index} className="flex items-center text-xs">
                                                                    {item.valid ? (
                                                                        <Check
                                                                            className="w-3 h-3 text-highland-500 mr-2 flex-shrink-0"/>
                                                                    ) : (
                                                                        <X className="w-3 h-3 text-murram-500 mr-2 flex-shrink-0"/>
                                                                    )}
                                                                    <span
                                                                        className={item.valid ? 'text-highland-600' : 'text-murram-600'}>
                                                                        {item.message}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Confirm Password */}
                                            <div>
                                                <label className="block text-xs font-medium text-soil-600 mb-1">
                                                    Confirm Password
                                                </label>
                                                <div className="relative">
                                                    <div
                                                        className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <Lock className="h-4 w-4 text-soil-400"/>
                                                    </div>
                                                    <input
                                                        name="confirmPassword"
                                                        type={showConfirmPassword ? 'text' : 'password'}
                                                        autoComplete="new-password"
                                                        value={formData.confirmPassword}
                                                        onChange={handleInputChange}
                                                        className={`block w-full pl-10 pr-10 py-2 border rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent transition-colors ${
                                                            errors.confirmPassword ? 'border-murram-500' : 'border-soil-100'
                                                        }`}
                                                        placeholder="Confirm your password"
                                                    />
                                                    <button
                                                        type="button"
                                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    >
                                                        {showConfirmPassword ? (
                                                            <EyeOff className="h-4 w-4 text-soil-400"/>
                                                        ) : (
                                                            <Eye className="h-4 w-4 text-soil-400"/>
                                                        )}
                                                    </button>
                                                </div>
                                                {errors.confirmPassword && (
                                                    <p className="mt-1 text-xs text-murram-600">{errors.confirmPassword}</p>
                                                )}
                                            </div>
                                        </>
                                    )}

                                    {/* Step 3: Additional Info & Terms */}
                                    {currentStep === 3 && (
                                        <>
                                            {/* Location */}
                                            <div>
                                                <label className="block text-xs font-medium text-soil-600 mb-1">
                                                    Location
                                                </label>
                                                <div className="relative">
                                                    <div
                                                        className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <MapPin className="h-4 w-4 text-soil-400"/>
                                                    </div>
                                                    <select
                                                        name="location"
                                                        value={formData.location}
                                                        onChange={handleInputChange}
                                                        className={`block w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent transition-colors ${
                                                            errors.location ? 'border-murram-500' : 'border-soil-100'
                                                        }`}
                                                    >
                                                        <option value="">Select your city</option>
                                                        {kenyanCities.map(city => (
                                                            <option key={city}
                                                                    value={city.toLowerCase()}>{city}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                {errors.location && (
                                                    <p className="mt-1 text-xs text-murram-600">{errors.location}</p>
                                                )}
                                            </div>

                                            {/* Terms and Conditions */}
                                            <div className="space-y-1.5">
                                                <div className="flex items-start">
                                                    <input
                                                        name="agreeToTerms"
                                                        type="checkbox"
                                                        checked={formData.agreeToTerms}
                                                        onChange={handleInputChange}
                                                        className="mt-0.5 h-3.5 w-3.5 text-highland-500 focus:ring-highland-500 border-soil-100 rounded"
                                                    />
                                                    <label className="ml-2 block text-xs text-soil-900">
                                                        I agree to the{' '}
                                                        <button
                                                            type="button"
                                                            onClick={() => handleNavigate('/terms-of-service')}
                                                            className="font-medium text-highland-500 hover:text-highland-600"
                                                        >
                                                            Terms of Service
                                                        </button>
                                                    </label>
                                                </div>
                                                {errors.agreeToTerms && (
                                                    <p className="text-xs text-murram-600">{errors.agreeToTerms}</p>
                                                )}

                                                <div className="flex items-start">
                                                    <input
                                                        name="agreeToPrivacy"
                                                        type="checkbox"
                                                        checked={formData.agreeToPrivacy}
                                                        onChange={handleInputChange}
                                                        className="mt-0.5 h-3.5 w-3.5 text-highland-500 focus:ring-highland-500 border-soil-100 rounded"
                                                    />
                                                    <label className="ml-2 block text-xs text-soil-900">
                                                        I agree to the{' '}
                                                        <button
                                                            type="button"
                                                            onClick={() => handleNavigate('/privacy-policy')}
                                                            className="font-medium text-highland-500 hover:text-highland-600"
                                                        >
                                                            Privacy Policy
                                                        </button>
                                                    </label>
                                                </div>
                                                {errors.agreeToPrivacy && (
                                                    <p className="text-xs text-murram-600">{errors.agreeToPrivacy}</p>
                                                )}

                                                <div className="flex items-start">
                                                    <input
                                                        name="subscribeNewsletter"
                                                        type="checkbox"
                                                        checked={formData.subscribeNewsletter}
                                                        onChange={handleInputChange}
                                                        className="mt-0.5 h-3.5 w-3.5 text-highland-500 focus:ring-highland-500 border-soil-100 rounded"
                                                    />
                                                    <label className="ml-2 block text-xs text-soil-900">
                                                        Subscribe to newsletter for updates and property alerts
                                                    </label>
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {/* Navigation Buttons */}
                                    <div className="flex justify-between pt-2">
                                        {currentStep > 1 ? (
                                            <button
                                                type="button"
                                                onClick={handlePrevious}
                                                className="flex items-center px-3 py-1.5 text-sm border border-soil-100 rounded-lg text-soil-600 hover:bg-soil-50 transition-colors duration-200"
                                            >
                                                <ArrowRight className="w-4 h-4 mr-2 rotate-180"/>
                                                Previous
                                            </button>
                                        ) : (
                                            <div/>
                                        )}

                                        {currentStep < 3 ? (
                                            <button
                                                type="button"
                                                onClick={handleNext}
                                                className="group relative flex justify-center py-2 px-5 border border-transparent text-sm font-medium rounded-lg text-white bg-highland-500 hover:bg-highland-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-highland-500 transition-all duration-200"
                                            >
                                                Next
                                                <ArrowRight
                                                    className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-200"/>
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={handleSubmit}
                                                disabled={isLoading}
                                                className="group relative flex justify-center py-2 px-5 border border-transparent text-sm font-medium rounded-lg text-white bg-highland-500 hover:bg-highland-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-highland-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                                            >
                                                {isLoading ? (
                                                    <>
                                                        <Loader className="w-4 h-4 mr-2 animate-spin"/>
                                                        Creating Account...
                                                    </>
                                                ) : (
                                                    <>
                                                        Create Account
                                                        <CheckCircle
                                                            className="w-4 h-4 ml-2 group-hover:scale-110 transition-transform duration-200"/>
                                                    </>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Sign In Link */}
                                <div className="mt-3 text-center">
                                    <p className="text-xs text-soil-600">
                                        Already have an account?{' '}
                                        <button
                                            onClick={() => handleNavigate('/login')}
                                            className="font-medium text-highland-500 hover:text-highland-600"
                                        >
                                            Sign in here
                                        </button>
                                    </p>
                                </div>

                                {/* Back to Home */}
                                <div className="mt-1.5 text-center">
                                    <button
                                        onClick={() => handleNavigate('/')}
                                        className="inline-flex items-center text-xs text-soil-400 hover:text-soil-600"
                                    >
                                        <ArrowRight className="w-3.5 h-3.5 mr-1 rotate-180"/>
                                        Back to Home
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Register;